import { useState, useEffect, useRef } from 'react';
import {
  formatDateString,
  parseRangeString,
  computeInitialDates,
  computeMinMaxDates,
  computeStaticRanges,
  clampSelectionToDateThresholdsAndMaxSpan,
} from './dateRangeUtils';

/**
 * Custom hook to encapsulate outside click events.
 */
const useClickOutside = (ref, onOutsideClick) => {
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        onOutsideClick();
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [ref, onOutsideClick]);
};

/**
 * Custom hook to manage date range selection state, validation/clamping conditions, and popover state.
 */
export const useDateRangePicker = ({
  startDate,
  endDate,
  value,
  onChange,
  disableFutureDates = false,
  disablePastDates = false,
  disableMonthPresets = true,
  minDate: minDateThreshold,
  maxDate: maxDateThreshold,
  staticRanges: customStaticRanges,
  maxDays = 7,
  maxRangeDays,
  placeholder = 'Select Date Range',
  defaultToWeekToday = false,
  defaultToPastWeek = false,
}) => {
  const containerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  const initialDates = computeInitialDates({
    startDate,
    endDate,
    value,
    defaultToPastWeek: defaultToWeekToday || defaultToPastWeek,
    minDateThreshold,
  });

  const [selectedRange, setSelectedRange] = useState([
    {
      startDate: initialDates.start,
      endDate: initialDates.end,
      key: 'selection',
    },
  ]);

  const [draftRange, setDraftRange] = useState(selectedRange);

  const { minDate, maxDate } = computeMinMaxDates({
    disablePastDates,
    disableFutureDates,
    minDateThreshold,
    maxDateThreshold,
  });

  const effectiveStaticRanges = computeStaticRanges(disableMonthPresets, customStaticRanges);

  // Sync external changes (startDate, endDate, value)
  useEffect(() => {
    if (startDate && endDate) {
      const parsedStart = new Date(startDate);
      const parsedEnd = new Date(endDate);
      if (!isNaN(parsedStart.getTime()) && !isNaN(parsedEnd.getTime())) {
        const newRange = [{ startDate: parsedStart, endDate: parsedEnd, key: 'selection' }];
        setSelectedRange(newRange);
        setDraftRange(newRange);
      }
    } else {
      const parsedRange = parseRangeString(value);
      if (parsedRange) {
        const newRange = [{ startDate: parsedRange.startDate, endDate: parsedRange.endDate, key: 'selection' }];
        setSelectedRange(newRange);
        setDraftRange(newRange);
      }
    }
  }, [value, startDate, endDate]);

  // Adjust selection if minimum date threshold changes
  const minDateThresholdTimestamp =
    minDateThreshold instanceof Date && !isNaN(minDateThreshold.getTime())
      ? minDateThreshold.getTime()
      : null;

  useEffect(() => {
    if (minDateThresholdTimestamp) {
      const activeMinThresholdDate = new Date(minDateThresholdTimestamp);
      setSelectedRange((previousRange) => {
        const currentSelection = previousRange[0];
        const isStartBeforeMinThreshold =
          currentSelection?.startDate && currentSelection.startDate < activeMinThresholdDate;
        if (isStartBeforeMinThreshold) {
          const clampedSelection = { ...currentSelection, startDate: activeMinThresholdDate };
          setDraftRange([clampedSelection]);
          return [clampedSelection];
        }
        return previousRange;
      });
    }
  }, [minDateThresholdTimestamp]);

  const handleConfirmSelection = () => {
    if (isOpen) {
      const confirmedSelection = draftRange[0] || {};
      setSelectedRange(draftRange);
      setIsOpen(false);

      if (confirmedSelection.startDate && confirmedSelection.endDate && onChange) {
        const formattedStartDate = formatDateString(confirmedSelection.startDate);
        const formattedEndDate = formatDateString(confirmedSelection.endDate);
        const formattedDateRange = `${formattedStartDate} - ${formattedEndDate}`;

        onChange({
          startDate: confirmedSelection.startDate,
          endDate: confirmedSelection.endDate,
          formattedStartDate,
          formattedEndDate,
          startStr: formattedStartDate,
          endStr: formattedEndDate,
          rangeString: formattedDateRange,
          value: formattedDateRange,
        });
      }
    }
  };

  useClickOutside(containerRef, () => {
    if (isOpen) {
      handleConfirmSelection();
    }
  });

  const handleDraftSelectionChange = (pickerRangeItem) => {
    const maxAllowedSpanInDays = maxRangeDays || maxDays;
    const clampedSelection = clampSelectionToDateThresholdsAndMaxSpan(
      { ...pickerRangeItem.selection },
      maxAllowedSpanInDays,
      maxDate,
      minDate
    );

    setDraftRange([clampedSelection]);
  };

  const handleTogglePopover = () => {
    if (!isOpen) {
      setDraftRange(selectedRange);
      setIsOpen(true);
    } else {
      handleConfirmSelection();
    }
  };

  const activeRange = (isOpen ? draftRange[0] : selectedRange[0]) || {};
  const displayLabel =
    activeRange.startDate && activeRange.endDate
      ? `${formatDateString(activeRange.startDate)} - ${formatDateString(activeRange.endDate)}`
      : value || placeholder;

  return {
    containerRef,
    isOpen,
    draftRange,
    minDate,
    maxDate,
    effectiveStaticRanges,
    displayLabel,
    handleTogglePopover,
    handleConfirmSelection,
    handleDraftSelectionChange,
  };
};

export default useDateRangePicker;
